import {config} from './config'
import {create} from 'apisauce'
import utils from '@eitje/utils'
import _ from 'lodash'
import moment from 'moment'

let api
const createApi = () => {
	api = create({
		baseURL: config.baseURL,
		headers: {
			'Content-Type': 'application/json',
			credentials: 'same-origin',
			'Access-Control-Allow-Origin': '*',
		},
		...config.apiConfig,
	})

	api.addAsyncRequestTransform(request => setVersion(request))
	api.addAsyncRequestTransform(request => changeTokenHeader(request))
	api.addRequestTransform(request => startLoad(request))
	api.addRequestTransform(sanitizeParams)

	api.addMonitor(authMonitor)
	api.addMonitor(endLoad)
	api.addMonitor(handleErrors)
	if (config.success) api.addMonitor(reportSuccess)

	return api
}

export const sanitizeMoment = v => (v instanceof moment ? v.format('YYYY-MM-DD') : v)

const sanitizeParams = request => {
	const {headers = {}} = request
	const isFormData = typeof FormData !== 'undefined' && request.data instanceof FormData
	if (isFormData) {
		headers['Content-Type'] = 'multipart/form-data'
		return
	}

	const contentType = headers['Content-Type']
	if (contentType == 'multipart/form-data') return

	request.params = _sanitizeParams(request.params)
	request.data = _sanitizeParams(request.data)
}

export function deepTransformValues(value, mapper) {
	if (moment.isMoment(value)) return mapper(value)
	if (_.isDate(value)) return mapper(value)
	if (Array.isArray(value)) return value.map(item => deepTransformValues(item, mapper))
	if (typeof value === 'object' && value !== null) return _.mapValues(value, item => deepTransformValues(item, mapper))
	return mapper(value)
}

const _sanitizeParams = obj => {
	if (!obj) return
	return deepTransformValues(obj, sanitizeMoment)
}

async function changeTokenHeader(req) {
	if (req.url !== 'oauth/token' && req.url !== 'auth' && req.url !== 'users/sign_up' && req.url !== 'auth/confirmed') {
		const token = await config.getRefreshToken()
		if (token) {
			if (!req.params) req.params = {}
			if (req.params) req.params['access_token'] = token
		}
	}
}

async function setVersion(req) {
	if (!req.params) req.params = {}
	req.params['version'] = config.version
}

const authMonitor = res => {
	if (res.status === 401 && !res.config.url.match(/auth/)) {
		config.logout()
	}
}

const getData = req => {
	const {params = {}, data = {}} = req
	return {...params, ...data}
}

const indexRegex = /\/index$/

const isIndexUrl = req => {
	const url = req.config?.url || req.url
	if (!url) return
	return url.match(indexRegex)
}

const startLoad = req => {
	const data = getData(req)
	const isMultiPart = req.headers['Content-Type'] === 'multipart/form-data'
	if (isIndexUrl(req) && !data['doLoad']) return

	if (
		data.doLoad ||
		req.headers['doLoad'] ||
		(req.method !== 'get' && !isMultiPart && !req.headers['doNotLoad'] && (!data || !data.doNotLoad))
	) {
		config.store && config.store.dispatch({type: 'START_LOADING'})
	}
}

const endLoad = req => {
	const data = getDataForMonitor(req) || {}
	if (isIndexUrl(req) && !data['doLoad']) return
	if (data.doLoad || (req.config.method !== 'get' && !data.doNotLoad)) {
		config.store.dispatch({type: 'STOP_LOADING'})
	}
}

function setErrors(errors) {
	errors = utils.alwaysDefinedArray(errors)
	errors.forEach(reportError)
}

const reportError = errors => {
	const hasError = _.isObject(errors) && Object.keys(errors).length > 0
	let err
	if (hasError) {
		const errs = _.flatten(Object.values(errors))
		err = errs[0]
	} else {
		err = _.isString(errors) ? errors : config.t('unexpectedIssue')
	}
	if (!_.isString(err)) return
	config.alert(config.t('oops'), err)
}

const restStatusses = [410]

function handleErrors(res) {
	const {t, alert} = config
	if (restStatusses.includes(res.status)) return // these errors are resource-specific and thus handled by my higher-level counterpart

	if (res.problem === 'NETWORK_ERROR') return

	if (res.status === 403) {
		const msg = res?.data?.message || t('unauthorized')
		alert(t('oops'), msg)
		return
	}

	let errs = res.data?.errors || res.errors
	if (res.status > 400 && !errs) errs = res.data

	if (!config.isProd && errs?.exception) return config.alert(config.t('oops'), errs.exception)

	if (res.status === 502) {
		config.alert(config.t('oops'), config.t('errors.server_down'))
		return
	}

	if (errs && !errs?.exception) {
		setErrors(errs)
		return
	}
	if (res.status <= 401) return

	config.alert(config.t('oops'), config.t('unexpectedIssue'))
}

function reportSuccess(req) {
	if (isIndexUrl(req)) return
	const data = getDataForMonitor(req)
	if (req.config.method != 'get' && req.ok && req.status <= 300 && !data?.doNotLoad && !data?.skipSuccessNotif) {
		config.success()
	}
}

const getDataForMonitor = req => {
	let data

	try {
		data = req.config.data ? JSON.parse(req.config.data) : req.config.params
	} catch {
		data = req.config.params
	}

	return data
}

export default createApi
export {api, api as backend}
